import { buildAndDefineMetaMap, ClassType, FieldDecorator, getMetaMapValues, Lazy } from '@nx-data/utilities';
import { Entity } from '../model';

/* eslint-disable prefer-arrow/prefer-arrow-functions */

const metadataKey = Symbol('Join');
type Cardinality = 'one' | 'many';

export class JoinDecoratorMetadata<T extends Entity, S extends Entity> {
  constructor(
    private readonly _joinedEntityOrLazy: ClassType<T> | Lazy<ClassType<T>>,
    public readonly propertyKey: string,
    public readonly selfProperty: JoinableProperty<S>,
    public readonly targetProperty: JoinableProperty<T>,
    public readonly cardinality: Cardinality
  ) {}

  public get joinedEntity(): ClassType<T> {
    return this._joinedEntityOrLazy instanceof Lazy ? this._joinedEntityOrLazy.get() : this._joinedEntityOrLazy;
  }
}

type JoinableProperty<T> = keyof {
  [K in keyof T as T[K] extends string | null | undefined ? K : T[K] extends Array<string> ? K : never]: any;
};

export function joinKey<T>(entity: ClassType<T>, key: JoinableProperty<T>): JoinableProperty<T> {
  return key;
}

export function Join<T extends Entity, S extends Entity>(
  targetClassType: ClassType<T> | Lazy<ClassType<T>>,
  selfProperty: JoinableProperty<S>,
  targetProperty: JoinableProperty<T>,
  cardinality: Cardinality
): FieldDecorator {
  return function (target: object, propertyKey: string) {
    if (targetClassType == null) {
      throw Error('Circular class type dependencies should be lazy');
    }

    buildAndDefineMetaMap(
      metadataKey,
      target,
      propertyKey,
      new JoinDecoratorMetadata(targetClassType, propertyKey, selfProperty, targetProperty, cardinality)
    );
  };
}

export function JoinOne<Self extends Entity, Target extends Entity>(
  targetClassType: ClassType<Target> | Lazy<ClassType<Target>>,
  targetProperty: JoinableProperty<Target>,
  selfClassType: ClassType<Self>,
  selfProperty: JoinableProperty<Self>
): FieldDecorator {
  return Join<Target, Self>(targetClassType, selfProperty, targetProperty, 'one');
}

export function JoinMany<Self extends Entity, Target extends Entity>(
  targetClassType: ClassType<Target> | Lazy<ClassType<Target>>,
  targetProperty: JoinableProperty<Target>,
  selfClassType: ClassType<Self>,
  selfProperty: JoinableProperty<Self>
): FieldDecorator {
  return Join<Target, Self>(targetClassType, selfProperty, targetProperty, 'many');
}

export function getJoinMetadata<T extends Entity>(classType: ClassType<T>) {
  return getMetaMapValues<JoinDecoratorMetadata<Entity, T>>(metadataKey, classType.prototype);
}
